import React, { useState } from 'react';
import { AlertCircleIcon, Loader2Icon, WifiOffIcon } from 'lucide-react';
import { Button } from '../../ui/Button';
import { SelectField, TextField } from '../../ui/Field';
import { Sheet } from '../Sheet';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { useOnline } from '../../../hooks/useSyncStatus';
import type { CaseEntry, CaseEntryUnit } from '../../../types/caseEntry';
import { expenseTypes, travelTypes, useCaseEntryForm } from './useCaseEntryForm';
import type { EntryKind } from './useCaseEntryForm';
export interface EntryDialogTarget {
  caseId: string;
  kind: EntryKind;
  /** Sat når en eksisterende linje rettes. */
  entry?: CaseEntry;
}
const titles: Record<EntryKind, string> = {
  hours: 'Registrér timer',
  material: 'Tilføj materiale',
  travel: 'Tilføj kørsel/service',
  expense: 'Tilføj udgift'
};
const placeholders: Record<EntryKind, string> = {
  hours: 'Fx belægningsarbejde',
  material: 'Fx kantskinne',
  travel: 'Fx kørsel til og fra opgaven',
  expense: 'Fx gasbrænder eller jord til genbrugsplads'
};
const materialUnits: CaseEntryUnit[] = ['stk.', 'meter', 'm²', 'fast pris'];
const expenseUnits: CaseEntryUnit[] = ['stk.', 'timer', 'meter', 'm²', 'fast pris'];
interface EntryDialogProps {
  target: EntryDialogTarget | null;
  onClose: () => void;
  onSaved?: () => void;
}
export function EntryDialog({
  target,
  onClose,
  onSaved
}: EntryDialogProps) {
  const title = target ? target.entry ? 'Ret linje' : titles[target.kind] : '';
  return <Sheet open={Boolean(target)} title={title} onClose={onClose} data-id="element-162" data-mp="qwi9qyt9f2gu">
      {target && <EntryDialogForm key={`${target.caseId}-${target.kind}-${target.entry?.id ?? 'ny'}`} target={target} onDone={() => {
      onSaved?.();
      onClose();
    }} data-id="element-163" data-mp="14vhwu214j9aw" />}
    </Sheet>;
}
function EntryDialogForm({
  target,
  onDone
}: {
  target: EntryDialogTarget;
  onDone: () => void;
}) {
  const {
    cases,
    updateCase,
    settings
  } = useAdminData();
  const caseItem = cases.find(item => item.id === target.caseId);
  const {
    kind,
    entry
  } = target;
  const {
    form,
    setField,
    build,
    formError
  } = useCaseEntryForm(kind, caseItem?.hourlyRate || settings.hourlyRate || '', entry);
  const online = useOnline();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (saving || !caseItem) return;
    const built = build();
    if (!built) return;
    // Altid ud fra sagens nyeste linjer; faste id'er gør et nyt forsøg dubletfrit.
    const current = caseItem.entries ?? [];
    const next = current.some(item => item.id === built.id) ? current.map(item => item.id === built.id ? built : item) : [...current, built];
    setSaving(true);
    setSaveError('');
    const result = await updateCase(caseItem.id, {
      entries: next
    });
    setSaving(false);
    if (!result.ok) {
      setSaveError(result.error ?? 'Linjen kunne ikke gemmes. Prøv igen.');
      return;
    }
    onDone();
  }
  if (!caseItem) {
    return <p className="text-sm text-ink-muted" data-id="element-164" data-mp="3v6lgt9xq3exk">Sagen blev ikke fundet.</p>;
  }
  return <form onSubmit={handleSubmit} noValidate className="space-y-4" data-id="element-165" data-mp="3r2nih1g8285k">
      {kind !== 'hours' && kind !== 'material' && <div role="group" aria-label="Type" className="flex flex-wrap gap-1.5" data-id="element-166" data-mp="3m43u6ue5oka1">
          {(kind === 'travel' ? travelTypes : expenseTypes).map(option => <button key={option} type="button" aria-pressed={form.subtype === option} onClick={() => setField('subtype', option)} className={`h-10 whitespace-nowrap rounded-full border px-4 text-sm transition-colors duration-150 ${form.subtype === option ? 'border-accent/60 bg-accent/15 text-ink' : 'border-line text-ink-soft hover:border-ink/30'}`} data-id="element-167" data-mp="3ceajrwpr2b7d">
              {option}
            </button>)}
        </div>}

      {kind === 'hours' && <TextField id="entry-dato" label="Dato" type="date" value={form.date} onChange={e => setField('date', e.target.value)} data-id="element-168" data-mp="12c68rntzi681" />}

      <TextField id="entry-beskrivelse" label="Beskrivelse" placeholder={placeholders[kind]} value={form.description} onChange={e => setField('description', e.target.value)} data-id="element-169" data-mp="nmqjkca3q8ug" />

      <div className="grid grid-cols-2 gap-3" data-id="element-170" data-mp="inxffagpfu6y">
        <TextField id="entry-antal" label={kind === 'hours' ? 'Timer' : form.subtype === 'Kørsel' ? 'Km' : 'Antal'} inputMode="decimal" placeholder={kind === 'hours' ? 'Fx 3,5' : '1'} value={form.quantity} onChange={e => setField('quantity', e.target.value)} data-id="element-171" data-mp="ioowzhdgvre8" />
        {(kind === 'material' || kind === 'expense') && <SelectField id="entry-enhed" label="Enhed" value={form.unit} onChange={e => setField('unit', e.target.value as CaseEntryUnit)} options={(kind === 'material' ? materialUnits : expenseUnits).map(unit => ({
        value: unit,
        label: unit
      }))} data-id="element-172" data-mp="240786pdsy8cn" />}
        <TextField id="entry-pris" label={kind === 'hours' ? 'Timepris (kr.)' : kind === 'material' ? 'Enhedspris (kr.)' : 'Pris (kr.)'} inputMode="decimal" placeholder="0" value={form.unitPrice} onChange={e => setField('unitPrice', e.target.value)} data-id="element-173" data-mp="2512opvwsfm7" />
        {kind === 'material' && <SelectField id="entry-moms" label="Moms" value={form.vatRate} onChange={e => setField('vatRate', e.target.value)} options={[{
        value: '25',
        label: '25 %'
      }, {
        value: '0',
        label: '0 %'
      }]} data-id="element-174" data-mp="l2u5ztag2rk4" />}
      </div>

      {(formError || saveError) && <p role="alert" className="flex items-start gap-2 text-sm text-danger" data-id="element-175" data-mp="37j9gpi3mllfn">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-176" data-mp="37lcj71lyt719" />
          {formError || saveError}
        </p>}

      {saving && !online && <p role="status" className="flex items-start gap-2 text-sm text-ink-soft" data-id="element-177" data-mp="3gi3br41b63r3">
          <WifiOffIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-178" data-mp="3ljk9iqohxa3g" />
          Venter på forbindelse – gemmes automatisk
        </p>}

      <Button type="submit" size="lg" className="w-full" disabled={saving} data-id="element-179" data-mp="3ujfqxd8na1bx">
        {saving ? <>
            <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-180" data-mp="351n7hfjm4mi" />
            Gemmer …
          </> : 'Gem'}
      </Button>
    </form>;
}